import { FC } from 'react';
import { Result, Select, Spin, Table } from 'antd';
import { green, red } from '@ant-design/colors';
import styled from 'styled-components';
import { PageContent } from '@shared/styled/page-content';
import { StyledActionBar } from '@shared/styled/action-bar';
import { AdvancedDateRangePicker } from '@shared/components/advanced-date-range-picker';
import { useCashRegisters, useCashFlowAnalytics } from '../hooks';

const AnalyticsTable = styled(Table)`
  border: 1px solid #f0f0f0;
  border-bottom: none;

  .ant-table-thead > tr > th {
    background-color: #fafafa;
  }

  tfoot > tr > td {
    background-color: #fafafa;
  }
` as typeof Table;

export const CashFlowAnalyticsPage: FC = () => {
  const { data: cashRegisters = [] } = useCashRegisters();
  const { result, filters, onCashRegisterIdChange, onDateChange } = useCashFlowAnalytics();

  const analyticsResult = result.data;
  const isLoading = result.isLoading;
  const error = result.error;

  const columns = [
    { key: 'category', title: 'Kateqoriya', dataIndex: 'name' },
    {
      width: 200,
      title: 'Gəlir',
      align: 'right' as const,
      key: 'income',
      dataIndex: 'income',
      render: (value: number) => (
        <b style={{ color: green[5] }}>
          {value?.toFixed(2)} {analyticsResult?.currency.code}
        </b>
      ),
    },
    {
      width: 200,
      title: 'Xərc',
      align: 'right' as const,
      key: 'expense',
      dataIndex: 'expense',
      render: (value: number) => (
        <b style={{ color: red[5] }}>
          {value?.toFixed(2)} {analyticsResult?.currency.code}
        </b>
      ),
    },
    {
      width: 150,
      key: 'percent',
      align: 'right' as const,
      dataIndex: 'percent',
      render: (value: number) => <b style={{ color: value > 0 ? green[5] : value < 0 ? red[5] : undefined }}>{value} %</b>,
    },
  ];

  const actionBar = (
    <StyledActionBar $flex={true}>
      <Select
        style={{ width: 200 }}
        value={filters.cashRegisterId}
        onChange={onCashRegisterIdChange}
        placeholder="Kassa seçin..."
        options={cashRegisters.map((r) => ({ value: r.id, label: r.name }))}
      />
      <AdvancedDateRangePicker value={filters.date} onChange={onDateChange} />
    </StyledActionBar>
  );

  if (error) {
    return (
      <PageContent>
        {actionBar}
        <Result status="500" title={error.message} />
      </PageContent>
    );
  }

  if (isLoading) {
    return (
      <PageContent>
        {actionBar}
        <Spin style={{ display: 'block', margin: '64px auto' }} />
      </PageContent>
    );
  }

  if (!analyticsResult || !analyticsResult.categories.length) {
    return (
      <PageContent>
        {actionBar}
        <Result status="404" title="Məlumat tapılmadı" />
      </PageContent>
    );
  }

  return (
    <PageContent>
      {actionBar}
      <AnalyticsTable
        rowKey="id"
        pagination={false}
        indentSize={8}
        expandable={{ expandRowByClick: true, indentSize: 16 }}
        dataSource={analyticsResult.categories}
        columns={columns}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>Ümumi</Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <b>
                {analyticsResult.total.income.toFixed(2)} {analyticsResult.currency.code}
              </b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">
              <b>
                {analyticsResult.total.expense.toFixed(2)} {analyticsResult.currency.code}
              </b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="right">
              <b>{analyticsResult.total.percent}%</b>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </PageContent>
  );
};
